import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { SidebarProvider } from './context/SidebarContext';
import { ModalProvider } from './context/ModalContext';
import PrivateRoute from './components/PrivateRoute';

// Landing Page
import Landing from './pages/Landing';

// Login Pages
import RestaurantLogin from './pages/RestaurantLogin';
import StaffLogin from './pages/StaffLogin';
import OwnerLogin from './pages/OwnerLogin';

// Restaurant Dashboard Pages
import RestaurantDashboard from './pages/restaurant/RestaurantDashboard';
import RestaurantGuests from './pages/restaurant/RestaurantGuests';
import RestaurantBroadcast from './pages/restaurant/RestaurantBroadcast';
import WhatsAppMenu from './pages/restaurant/WhatsAppMenu';
import MenuManagement from './pages/restaurant/MenuManagement';
import InventoryManagement from './pages/restaurant/InventoryManagement';
import POS from './pages/restaurant/POS';
import KDS from './pages/restaurant/KDS';
import Bills from './pages/restaurant/Bills';
import AdvancedAnalytics from './pages/restaurant/AdvancedAnalytics';
import StaffPerformance from './pages/restaurant/StaffPerformance';
import Tables from './pages/restaurant/Tables';
import Reservations from './pages/restaurant/Reservations';
import Waitlist from './pages/restaurant/Waitlist';
import FeedbackManagement from './pages/restaurant/FeedbackManagement';
import Settings from './pages/restaurant/Settings';
import StaffManagement from './pages/restaurant/StaffManagement';
import Accounting from './pages/restaurant/Accounting';

// Staff Pages
import StaffDashboard from './pages/staff/StaffDashboard';
import StaffOrders from './pages/staff/StaffOrders';
import StaffNotifications from './pages/staff/StaffNotifications';

// Shared Pages
import BirthdayAnniversary from './pages/shared/BirthdayAnniversary';
import QRMenu from './pages/shared/QRMenu';
import FeedbackForm from './pages/shared/FeedbackForm';

// Owner Pages
import OwnerDashboard from './pages/owner/OwnerDashboard';
import TenantDetails from './pages/owner/TenantDetails';

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <SidebarProvider>
          <ModalProvider>
            <Router>
          <Routes>
            {/* Landing Page */}
            <Route path="/" element={<Landing />} />
            
            {/* Login Routes */}
            <Route path="/login/restaurant" element={<RestaurantLogin />} />
            <Route path="/login/staff" element={<StaffLogin />} />
            <Route path="/login/owner" element={<OwnerLogin />} />
            <Route path="/login" element={<Navigate to="/" replace />} />

            {/* QR Menu (Public) */}
            <Route path="/menu/:tableId" element={<QRMenu />} />
            <Route path="/menu/tenant/:tenantSlug" element={<QRMenu />} />
            
            {/* Feedback Form (Public) */}
            <Route path="/feedback/:token" element={<FeedbackForm />} />

            {/* Restaurant Routes */}
            <Route
              path="/restaurant/dashboard"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <RestaurantDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/guests"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <RestaurantGuests />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/broadcast"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <RestaurantBroadcast />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/whatsapp-menu"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <WhatsAppMenu />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/menu"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <MenuManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/inventory"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <InventoryManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/pos"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin', 'pos_operator']}>
                  <POS />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/kds"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin', 'kitchen_staff']}>
                  <KDS />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/bills"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin', 'pos_operator']}>
                  <Bills />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/analytics"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <AdvancedAnalytics />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/tables"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <Tables />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/reservations"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin', 'staff']}>
                  <Reservations />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/waitlist"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin', 'staff']}>
                  <Waitlist />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/feedback"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <FeedbackManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/settings"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/staff"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <StaffManagement />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/staff-performance"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <StaffPerformance />
                </PrivateRoute>
              }
            />
            <Route
              path="/restaurant/accounting"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <Accounting />
                </PrivateRoute>
              }
            />

            {/* Staff Routes */}
            <Route
              path="/staff/dashboard"
              element={
                <PrivateRoute allowedRoles={['staff']}>
                  <StaffDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/staff/orders"
              element={
                <PrivateRoute allowedRoles={['staff']}>
                  <StaffOrders />
                </PrivateRoute>
              }
            />
            <Route
              path="/staff/kds"
              element={
                <PrivateRoute allowedRoles={['staff']}>
                  <KDS />
                </PrivateRoute>
              }
            />
            <Route
              path="/staff/notifications"
              element={
                <PrivateRoute allowedRoles={['staff']}>
                  <StaffNotifications />
                </PrivateRoute>
              }
            />
            <Route
              path="/staff/feedback"
              element={
                <PrivateRoute allowedRoles={['staff']}>
                  <Navigate to="/staff/orders" replace />
                </PrivateRoute>
              }
            />

            {/* Shared Routes */}
            <Route
              path="/birthday-anniversary"
              element={
                <PrivateRoute allowedRoles={['restaurant_admin']}>
                  <BirthdayAnniversary />
                </PrivateRoute>
              }
            />

            {/* Owner Routes */}
            <Route
              path="/owner/dashboard"
              element={
                <PrivateRoute>
                  <OwnerDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/owner/tenants/:id"
              element={
                <PrivateRoute>
                  <TenantDetails />
                </PrivateRoute>
              }
            />

            {/* Default Redirect - Already handled by Landing page */}
          </Routes>
        </Router>
          </ModalProvider>
        </SidebarProvider>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
